import React, { useState, useEffect, useContext } from "react";
import {
  MdSearch,
  MdRefresh,
  MdCheckCircle,
  MdCancel,
  MdWarning,
  MdConfirmationNumber,
} from "react-icons/md";
import { useHistory } from "react-router-dom";
import AutoCompleteField from "@components/AutoCompleteV2";
import UserContext from "@components/context/userContext";

import Menu from "@components/Menu";
import taloesService from "@services/taloes";
import empresasService from "@services/empresas";
import postosService from "@services/postos";
import tpvService from "@services/tpv";

import "./anulados.css";

function Anulados() {
  const profile = useContext(UserContext);
  const history = useHistory();

  const [searchForm, setSearchForm] = useState({
    posto: "",
    tpv: "",
    serie: "",
    talao: "",
  });

  const [empresa, setEmpresa] = useState(null);
  const [posto, setPosto] = useState(null);
  const [tpv, setTpv] = useState(null);

  const [empresas, setEmpresas] = useState([]);
  const [postos, setPostos] = useState([]);
  const [tpvs, setTpvs] = useState([]);
  const [ticketData, setTicketData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Controla quando os campos restantes devem estar desabilitados
  const areFieldsDisabled = !empresa || !posto || !tpv;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTicketData(null);
    setSearchForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const requiredFields = ["serie", "talao"];
    const fieldsValid = requiredFields.every((field) => searchForm[field].trim() !== "");
    return fieldsValid && empresa && posto && tpv;
  };

  const searchTicket = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Todos os campos são obrigatórios");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");
    setTicketData(null);

    try {
      const result = await taloesService.getAnuladoById(empresa.idempresa, searchForm);

      if (!result) {
        setError("Nenhum talão encontrado com os critérios fornecidos.");
        setIsLoading(false);
        return;
      }

      setTicketData(result);
    } catch (err) {
      console.error("Erro ao obter talão:", err);
      setError("Erro ao obter Talão");
    }

    setIsLoading(false);
  };

  const toggleAnulacao = async () => {
    if (!ticketData) return;

    setIsUpdating(true);
    setError("");
    setSuccessMessage("");

    try {
      await taloesService.setAnulado({
        idempresa: empresa.idempresa,
        posto: ticketData.posto,
        tpv: ticketData.tpv,
        serie: ticketData.serie,
        talao: ticketData.talao,
        anulado: !ticketData.anulado,
      });

      setTicketData((prev) =>
        prev ? { ...prev, anulado: !prev.anulado } : null
      );
      setSuccessMessage(
        ticketData.anulado
          ? "Talão reativado com sucesso!"
          : "Talão anulado com sucesso!"
      );
    } catch (err) {
      console.error("Erro ao alterar estado do talão:", err);
      setError("Erro ao alterar estado do talão");
    }

    setIsUpdating(false);
  };

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const fetchPostos = async (idempresa) => {
    if (!idempresa) {
      setPostos([]);
      return;
    }

    try {
      const result = await postosService.get(idempresa);
      console.log("Fetched postos:", result);
      setPostos(result || []);
    } catch (err) {
      console.error("Error fetching postos:", err);
      setPostos([]);
    }
  };

  const fetchTpvs = async (idempresa, postoId) => {
    if (!idempresa || !postoId) {
      setTpvs([]);
      return;
    }

    try {
      const result = await tpvService.get(idempresa, postoId);
      console.log("Fetched tpvs:", result);
      setTpvs(result || []);
    } catch (err) {
      console.error("Error fetching tpvs:", err);
      setTpvs([]);
    }
  };

  // Verificação de permissões
  useEffect(() => {
    if (profile["memberType"] < 10) {
      history.push("/");
    }
  }, [profile, history]);

  // Buscar empresas
  useEffect(() => {
    const fetchEmpresas = async () => {
      try {
        const result = await empresasService.get();
        console.log("Fetched empresas:", result);

        setEmpresas(result || []);
      } catch (err) {
        console.error("Error fetching empresas:", err);
      }
    };

    fetchEmpresas();
  }, []);

  return (
    <div>
      <Menu />
      <div
        className="container mx-auto px-4 py-8"
        style={{
          marginTop: "75px",
        }}
      >
        <div className="main-content">
          {/* Header */}
          <div className="header1">
            <div className="header1-title-container">
              <MdConfirmationNumber className="header1-icon" />
              <h1 className="header1-title">Gestão de Talões</h1>
            </div>
            <p className="header1-subtitle">
              Pesquise e gerencie o estado de anulação dos seus talões
            </p>
          </div>

          {/* Search Form */}
          <div className="card1">
            <h2 className="card1-title">
              <MdSearch className="card1-title-icon" />
              Pesquisar Talão
            </h2>

            <form onSubmit={searchTicket} className="form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="empresa" className="form-label">
                    Empresa *
                  </label>
                  <AutoCompleteField
                    options={empresas}
                    value={empresa}
                    onChange={(_, empresa) => {
                      setTicketData(null);
                      setEmpresa(empresa);
                      setPosto(null);
                      setTpv(null);
                      setSearchForm((prev) => ({
                        ...prev,
                        posto: "",
                        tpv: "",
                        serie: "",
                        talao: "",
                      }));
                      if (empresa && empresa.idempresa) {
                        fetchPostos(empresa.idempresa);
                      } else {
                        setPostos([]);
                        setTpvs([]);
                      }
                    }}
                    getOptionLabel={(option) =>
                      option.name || option.nomeempresa || option.nif || ""
                    }
                    label="Empresa"
                    searchableFields={["name", "nif", "nomeempresa", "id"]}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="posto" className="form-label">
                    Posto *
                  </label>
                  <AutoCompleteField
                    options={postos}
                    value={posto}
                    onChange={(_, selectedPosto) => {
                      setTicketData(null);
                      setPosto(selectedPosto);
                      setTpv(null);
                      setSearchForm((prev) => ({
                        ...prev,
                        posto: selectedPosto ? selectedPosto.posto || "" : "",
                        tpv: "",
                        serie: "",
                        talao: "",
                      }));
                      if (selectedPosto && empresa) {
                        fetchTpvs(empresa.idempresa, selectedPosto.posto);
                      } else {
                        setTpvs([]);
                      }
                    }}
                    getOptionLabel={(option) =>
                      option.nome || option.name || `Posto ${option.posto || option.id || ""}`
                    }
                    label="Posto"
                    searchableFields={["nome", "name", "posto", "id"]}
                    disabled={!empresa || postos.length === 0}
                    placeholder={!empresa ? "Selecione primeiro uma empresa" : "Código do posto"}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tpv" className="form-label">
                    TPV *
                  </label>
                  <AutoCompleteField
                    options={tpvs}
                    value={tpv}
                    onChange={(_, selectedTpv) => {
                      setTicketData(null);
                      setTpv(selectedTpv);
                      setSearchForm((prev) => ({
                        ...prev,
                        tpv: selectedTpv ? selectedTpv.tpv || "" : "",
                        serie: "",
                        talao: "",
                      }));
                    }}
                    getOptionLabel={(option) => option.tpv || ""}
                    label="TPV"
                    searchableFields={["tpv"]}
                    disabled={!posto || tpvs.length === 0}
                    placeholder={!posto ? "Selecione primeiro um posto" : "Terminal de pagamento"}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="serie" className="form-label">
                    Série *
                  </label>
                  <input
                    type="text"
                    id="serie"
                    name="serie"
                    value={searchForm.serie}
                    onChange={handleInputChange}
                    onFocus={clearMessages}
                    className="form-input"
                    placeholder={areFieldsDisabled ? "Selecione empresa, posto e TPV primeiro" : "Série do talão"}
                    disabled={areFieldsDisabled}
                  />
                </div>

                <div className="form-group form-grid-wide">
                  <label htmlFor="talao" className="form-label">
                    Talão *
                  </label>
                  <input
                    type="text"
                    id="talao"
                    name="talao"
                    value={searchForm.talao}
                    onChange={handleInputChange}
                    onFocus={clearMessages}
                    className="form-input"
                    placeholder={areFieldsDisabled ? "Selecione empresa, posto e TPV primeiro" : "Número do talão"}
                    disabled={areFieldsDisabled}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || areFieldsDisabled}
                className="btn btn-primary btn-full"
              >
                {isLoading ? (
                  <>
                    <MdRefresh className="btn-icon spinning" />
                    Pesquisando...
                  </>
                ) : (
                  <>
                    <MdSearch className="btn-icon" />
                    Pesquisar Talão
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Messages */}
          {error && (
            <div className="message message-error">
              <MdWarning className="message-icon" />
              {error}
            </div>
          )}

          {successMessage && (
            <div className="message message-success">
              <MdCheckCircle className="message-icon" />
              {successMessage}
            </div>
          )}

          {/* Ticket Results */}
          {ticketData && (
            <div className="card1">
              <h2 className="card1-title">
                <MdConfirmationNumber className="card1-title-icon" />
                Informações do Talão
              </h2>

              <div className="ticket-info">
                <div className="ticket-details">
                  <div className="detail-row">
                    <span className="detail-label">Empresa:</span>
                    <span className="detail-value">
                      {empresa.name || empresa.nomeempresa || empresa.nif || ""}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Cliente:</span>
                    <span className="detail-value">
                      {ticketData.cliente
                        ? ticketData.nome_1
                        : "Consumidor Final"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Data Emissão:</span>
                    <span className="detail-value">
                      {ticketData.data} {ticketData.hora}
                    </span>
                  </div>
                </div>

                <div className="ticket-details">
                  <div className="detail-row">
                    <span className="detail-label">Posto:</span>
                    <span className="detail-value">{ticketData.posto}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">TPV:</span>
                    <span className="detail-value">{ticketData.tpv}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Série:</span>
                    <span className="detail-value">{ticketData.serie}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Talão:</span>
                    <span className="detail-value">{ticketData.talao}</span>
                  </div>
                </div>
              </div>

              {/* Status and Action */}
              <div className="ticket-status-section">
                <div className="status-header1">
                  <div className="status-info">
                    <span className="status-label">Estado:</span>
                    <div
                      className={`status-badge ${
                        ticketData.anulado ? "cancelled" : "active"
                      }`}
                    >
                      {ticketData.anulado ? (
                        <>
                          <MdCancel className="status-badge-icon" />
                          Anulado
                        </>
                      ) : (
                        <>
                          <MdCheckCircle className="status-badge-icon" />
                          Ativo
                        </>
                      )}
                    </div>
                  </div>

                  <div className="value-info">
                    <p className="value-label">Valor Total:</p>
                    <p className="value-amount">
                      €{parseFloat(ticketData.valor_liquido).toFixed(2)}
                    </p>
                  </div>
                </div>

                <button
                  onClick={toggleAnulacao}
                  disabled={isUpdating}
                  className={`btn btn-full ${
                    ticketData.anulado ? "btn-success" : "btn-danger"
                  }`}
                >
                  {isUpdating ? (
                    <>
                      <MdRefresh className="btn-icon spinning" />
                      Processando...
                    </>
                  ) : (
                    <>
                      {ticketData.anulado ? (
                        <>
                          <MdCheckCircle className="btn-icon" />
                          Retirar Anulação
                        </>
                      ) : (
                        <>
                          <MdCancel className="btn-icon" />
                          Anular Talão
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Anulados;
